import React, { useEffect, useMemo, useState } from 'react';
import { Inbox, RefreshCw, Trash2, Phone, Mail, MapPin } from 'lucide-react';
import { useSRMS } from '../../context/SRMSContext';
import { firebaseFirestoreService } from '../../services/firebaseFirestoreService';
import { Enquiry } from '../../types';
import { displayDate } from '../../utils/dates';

/**
 * ENQUIRIES FROM THE "JOIN US" FORM ON scalesupport.co.in.
 *
 * An agency or a bank fills the form in without being signed in -- the only
 * write in the whole database that does not require a login, and the rules
 * allow a create and nothing else. This is where those arrive.
 *
 * FETCHED WHEN OPENED, NOT SUBSCRIBED. An enquiry is read when somebody
 * looks, not watched while nobody is. One read of a small collection, with
 * a Refresh button for when you are expecting one.
 *
 * The status is yours to move: new -> contacted -> empanelled, or declined.
 * Nothing moves it automatically, because nothing else knows whether you
 * have rung them.
 */

const STATUSES: Array<{ id: Enquiry['status']; label: string; cls: string }> = [
  { id: 'new', label: 'New', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  { id: 'contacted', label: 'Contacted', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  { id: 'empanelled', label: 'Empanelled', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  { id: 'declined', label: 'Declined', cls: 'bg-slate-200 text-slate-700 border-slate-300' },
];

const EnquiriesPanel: React.FC = () => {
  const { currentUser } = useSRMS();

  const isAdmin =
    currentUser?.role === 'admin' || (currentUser?.role as string) === 'super_admin';

  const [rows, setRows] = useState<Enquiry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [filter, setFilter] = useState<'ALL' | Enquiry['status']>('ALL');
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    setLoading(true);
    firebaseFirestoreService
      .fetchEnquiries()
      .then((list) => {
        if (!cancelled) setRows(list || []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, reloadKey]);

  const shown = useMemo(() => {
    const list = rows || [];
    return filter === 'ALL' ? list : list.filter((r) => r.status === filter);
  }, [rows, filter]);

  const counts = useMemo(() => {
    const out: Record<string, number> = { ALL: (rows || []).length };
    STATUSES.forEach((s) => {
      out[s.id] = (rows || []).filter((r) => r.status === s.id).length;
    });
    return out;
  }, [rows]);

  if (!isAdmin) return null;

  const setStatus = async (row: Enquiry, status: Enquiry['status']) => {
    setBusyId(row.id);
    try {
      await firebaseFirestoreService.updateEnquiryStatus(row.id, status);
      setRows((prev) => (prev || []).map((r) => (r.id === row.id ? { ...r, status } : r)));
    } catch (err: any) {
      window.alert(`Could not update that enquiry: ${err?.message || 'unknown error'}`);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (row: Enquiry) => {
    if (!window.confirm(`Delete the enquiry from "${row.agencyName}"? This cannot be undone.`)) {
      return;
    }
    setBusyId(row.id);
    try {
      await firebaseFirestoreService.deleteEnquiry(row.id);
      setRows((prev) => (prev || []).filter((r) => r.id !== row.id));
    } catch (err: any) {
      window.alert(`Could not delete that enquiry: ${err?.message || 'unknown error'}`);
    } finally {
      setBusyId(null);
    }
  };

  const when = (iso?: string) => {
    const s = (iso || '').trim();
    if (!s) return '—';
    const day = s.slice(0, 10);
    const time = s.slice(11, 16);
    return `${displayDate(day)}${time ? ` · ${time}` : ''}`;
  };

  return (
    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
      <div className="flex items-center gap-2">
        <Inbox className="w-4 h-4 text-blue-600" />
        <div>
          <span className="font-bold text-slate-900 block">Website enquiries</span>
          <span className="text-slate-500 text-[11px]">
            Sent from the “Join Us” form on scalesupport.co.in. Read when you open this
            panel, not watched in the background.
          </span>
        </div>
      </div>

      <div className="p-3 bg-white rounded-xl border border-slate-200 space-y-2.5 text-[11px]">
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setFilter('ALL')}
            className={`px-2.5 py-1.5 rounded-lg font-bold border cursor-pointer ${
              filter === 'ALL'
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
            }`}
          >
            All ({counts.ALL})
          </button>
          {STATUSES.map((s) => (
            <button
              key={s.id}
              onClick={() => setFilter(s.id)}
              className={`px-2.5 py-1.5 rounded-lg font-bold border cursor-pointer ${
                filter === s.id
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
              }`}
            >
              {s.label} ({counts[s.id] ?? 0})
            </button>
          ))}

          <button
            onClick={() => setReloadKey((k) => k + 1)}
            disabled={loading}
            className="ml-auto px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1.5 shrink-0 cursor-pointer"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            <span>{loading ? 'Loading…' : 'Refresh'}</span>
          </button>
        </div>

        {rows === null ? (
          <div className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-600 font-semibold">
            Loading enquiries…
          </div>
        ) : shown.length === 0 ? (
          <div className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-600 font-semibold">
            {(rows || []).length === 0
              ? 'No enquiries yet. They arrive here the moment somebody fills in the form on the website.'
              : 'No enquiries with that status.'}
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
            {shown.map((r) => {
              const badge = STATUSES.find((s) => s.id === r.status) || STATUSES[0];
              return (
                <div key={r.id} className="p-3 space-y-2">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-bold text-slate-900 text-xs truncate">
                        {r.agencyName}
                      </div>
                      <div className="text-slate-500">{when(r.createdAt)}</div>
                    </div>
                    <span
                      className={`px-2 py-0.5 rounded-full font-bold border shrink-0 ${badge.cls}`}
                    >
                      {badge.label}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-slate-700">
                    <span className="flex items-center gap-1.5">
                      <Phone className="w-3 h-3 text-slate-400" />
                      <strong className="font-mono">{r.mobile || '—'}</strong>
                    </span>
                    {r.email && (
                      <span className="flex items-center gap-1.5 min-w-0">
                        <Mail className="w-3 h-3 text-slate-400 shrink-0" />
                        <span className="truncate">{r.email}</span>
                      </span>
                    )}
                    {r.city && (
                      <span className="flex items-center gap-1.5 min-w-0">
                        <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
                        <span className="truncate">{r.city}</span>
                      </span>
                    )}
                  </div>

                  {r.message && (
                    <p className="text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2">
                      {r.message}
                    </p>
                  )}

                  <div className="flex flex-wrap items-center gap-1.5">
                    {STATUSES.filter((s) => s.id !== r.status).map((s) => (
                      <button
                        key={s.id}
                        disabled={busyId === r.id}
                        onClick={() => setStatus(r, s.id)}
                        className="px-2 py-1 rounded-lg font-bold border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 disabled:opacity-50 cursor-pointer"
                      >
                        Mark {s.label.toLowerCase()}
                      </button>
                    ))}
                    <button
                      disabled={busyId === r.id}
                      onClick={() => remove(r)}
                      title="Delete this enquiry"
                      className="ml-auto p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg disabled:opacity-50 cursor-pointer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default EnquiriesPanel;
